import React from "react";
import { Info } from "lucide-react";
import { AppButton, Callout, Checkbox, ColorInput, Label } from "../ui";
import { DEFAULT_BRAND_HEX } from "../../utils/applyBrandTokens";
import BrandPreview from "./BrandPreview";

/**
 * Settings → Appearance → App colours.
 *
 * ── Why this block exists ────────────────────────────────────────────────────
 * The app used to read `InstituteSettings.primaryColor` — which is the MARKETING
 * SITE's colour — and applied it whenever the institute had set a brandName, a
 * logo or a favicon. Two things were wrong with that. Uploading a logo silently
 * switched on a colour override nobody had chosen, and there was no way to ask
 * for the product defaults back: blanking the picker just restored a hardcoded
 * fallback. This block is the institute's explicit answer to both.
 *
 * ⚠️ Nothing here reaches vc_cms. The public website keeps `primaryColor` and
 * its own presets; these values live in `InstituteSettings.appTheme` and are
 * deliberately absent from the `/site-config` payload the CMS reads.
 */

// The palette's own values, shown as the placeholder so "unset" previews what it
// will actually use. Keep in step with `styles/tokens/_palette.scss` §4.
const PALETTE_DEFAULTS = {
  primary: DEFAULT_BRAND_HEX,
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#f43f5e",
  info: "#0ea5e9",
};

const COLOR_FIELDS = [
  {
    key: "primary",
    label: "Brand colour",
    hint: "Buttons, links, active navigation, focus rings and highlights.",
  },
  {
    key: "success",
    label: "Success",
    hint: "Paid, completed, present, published.",
  },
  {
    key: "warning",
    label: "Warning",
    hint: "Pending, partial, expiring soon.",
  },
  {
    key: "danger",
    label: "Danger",
    hint: "Overdue, failed, absent, destructive actions.",
  },
  {
    key: "info",
    label: "Info",
    hint: "Due, scheduled, notices that are neither good nor bad.",
  },
];

const EMPTY_THEME = {
  mode: "default",
  primary: "",
  success: "",
  warning: "",
  danger: "",
  info: "",
};

export default function AppThemeSettings({ value, onChange, disabled = false }) {
  // Tolerant of the block being absent: a settings document written before this
  // existed has no `appTheme`, and that has to read as "defaults", not a crash.
  const theme = { ...EMPTY_THEME, ...(value || {}) };
  const isCustom = theme.mode === "custom";

  const set = (patch) => onChange({ ...theme, ...patch });

  const anyColourSet = COLOR_FIELDS.some((f) => theme[f.key]);

  return (
    <div className="settings-branding-section">
      <label className="settings-label">App Colours</label>
      <p className="settings-logo-hint" style={{ marginTop: 0 }}>
        Choose whether the LMS uses the product's default palette or your own colours. This affects
        the app only — your public website keeps its own theme.
      </p>

      {/* ⚠️ Checkbox, not Switch. A `role="switch"` promises "on/off, applied
          now"; this page has a Save button, so the promise here is "ticked, and
          saved when you submit" — which is what a checkbox says. */}
      <div className="tw-my-3">
        <Checkbox
          checked={isCustom}
          onChange={(e) =>
            // Switching back to default KEEPS the stored colours. An admin
            // toggling this to compare should not lose five picks; the server
            // ignores them entirely while the mode is 'default'.
            set({ mode: e.target.checked ? "custom" : "default" })
          }
          label="Use my own colours"
          description={
            isCustom
              ? "Anything you leave blank below keeps the product default."
              : "The app uses the product's default palette."
          }
          disabled={disabled}
        />
      </div>

      {isCustom && (
        <div className="tw-flex tw-flex-col tw-gap-3.5 tw-border-0 tw-border-l tw-border-solid tw-border-line tw-pl-4">
          {COLOR_FIELDS.map((field) => (
            <div key={field.key} className="tw-flex tw-flex-col tw-gap-1.5">
              <Label htmlFor={`app-theme-${field.key}`}>{field.label}</Label>
              <ColorInput
                id={`app-theme-${field.key}`}
                aria-label={field.label}
                value={theme[field.key]}
                fallback={PALETTE_DEFAULTS[field.key]}
                onChange={(hex) => set({ [field.key]: hex })}
                onReset={() => set({ [field.key]: "" })}
                disabled={disabled}
              />
              <span className="tw-text-[12px] tw-text-content-subtle">
                {field.hint}
                {!theme[field.key] && " Currently using the default."}
              </span>
            </div>
          ))}

          {anyColourSet && (
            <div>
              <AppButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  set({ primary: "", success: "", warning: "", danger: "", info: "" })
                }
                disabled={disabled}
              >
                Reset all to defaults
              </AppButton>
            </div>
          )}

          {/*
            The status families carry MEANING, not taste. An institute is free to
            match its brand's green, but "success is now purple" makes every
            screen in the product lie, so it is worth one sentence at the point
            of choosing rather than a support call afterwards.
          */}
          <Callout tone="info" icon={<Info size={14} />}>
            Success, warning, danger and info tell students what happened — keep them
            recognisably green, amber, red and blue. Chart colours are not listed here on
            purpose: they stay fixed so reports stay comparable between institutes.
          </Callout>
        </div>
      )}

      <div className="tw-mt-3">
        <BrandPreview appTheme={theme} />
      </div>
    </div>
  );
}
